import { isObject, isNil, first } from 'lodash-es'
import { TypedValueGetterParams } from '../gridUtils'
import { NegotiationPackageDetail, NegotiationOrAuditDetail, NegotiationDetail, NegotiationType } from 'api'
import { TypedContext, TypedRowNode } from 'view/components/datagrid'
import { isAonOrOcoOrSmartGroupCollection } from 'model/collectionUtils'
import { ValueGetterMapper } from 'selectors/negotiations'
import { CellClickedEvent } from 'ag-grid-community'

export const NEGOTIATIONS_PATH = 'negotiations'

const getCounterPartyData = (data: NegotiationPackageDetail, counterpartyId: string) => {
  if (!hasNegotiations(data)) {
    return undefined
  }

  return data[NEGOTIATIONS_PATH][counterpartyId]
}

const shouldDisplaySummaryRow = (negotiation: NegotiationPackageDetail) => isAonOrOcoOrSmartGroupCollection(negotiation.collection?.type)

const getData = (
  node: TypedRowNode<NegotiationPackageDetail | undefined>,
  data: NegotiationPackageDetail | undefined,
  context: TypedContext<NegotiationDetail>,
  counterpartyId: string
) => {
  if (node?.group) {
    const firstNode = first(node.allLeafChildren)

    if (isNil(firstNode) || isNil(firstNode.data) || !shouldDisplaySummaryRow(firstNode.data) || isNil(context.groupRowData)) {
      return undefined
    }

    const colId = node.rowGroupColumn?.getColId()

    if (isNil(colId) || isNil(context.groupRowData[colId])) {
      return undefined
    }

    const counterpartyData = getCounterPartyData(firstNode.data, counterpartyId)

    if (isNil(counterpartyData) || isNil(counterpartyData.negotiation.summaryNegotiationId)) {
      return undefined
    }

    return context.groupRowData[colId][counterpartyData.negotiation.summaryNegotiationId]
  } else {
    return data && getCounterPartyData(data, counterpartyId)
  }
}

const getSummaryNegotiationDetailsForCollection = (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
  if (!params.node.group) {
    return undefined
  }

  if (!params.node.allLeafChildren) {
    return undefined
  }

  // if any of the legs are excluded from best price calculations then exclude all summaries as well
  if (
    params.node.allLeafChildren.some(node => {
      const counterpartyNegotiations = node.data?.[NEGOTIATIONS_PATH] ? Object.values(node.data[NEGOTIATIONS_PATH]) : []
      return counterpartyNegotiations.some(negotiationDetail => negotiationDetail.excludeFromBestPriceCalculations)
    })
  ) {
    return undefined
  }

  const firstNode = first(params.node.allLeafChildren)

  if (isNil(firstNode) || isNil(firstNode.data) || !shouldDisplaySummaryRow(firstNode.data) || isNil(params.context.groupRowData)) {
    return undefined
  }

  const colId = params.node.rowGroupColumn?.getColId()

  if (isNil(colId) || isNil(params.context.groupRowData[colId])) {
    return undefined
  }

  const collectionId = firstNode.data?.negotiation.collectionId
  if (isNil(collectionId)) {
    return undefined
  }

  return Object.values(params.context.groupRowData[colId]).filter(
    negotiationPackageDetail => negotiationPackageDetail.negotiation.collectionId === collectionId
  )
}

const getContingentItemNegotiationDetailsForBestPriceComparison = (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
  if (!params.data?.[NEGOTIATIONS_PATH]) {
    return undefined
  }

  return Object.values(params.data[NEGOTIATIONS_PATH])
}

export const getNegotiationDetailsForBestPriceComparison = (params: TypedValueGetterParams<NegotiationPackageDetail>) => {
  return params.node.group
    ? getSummaryNegotiationDetailsForCollection(params)
    : getContingentItemNegotiationDetailsForBestPriceComparison(params)
}

export const createValueGetterCounterpartyDataMapper: (counterpartyId: string) => ValueGetterMapper = counterpartyId => params => {
  const { data, node, context } = params

  if (isNil(data) && isNil(node)) {
    return params
  }

  const mappedData = getData(node as TypedRowNode<NegotiationPackageDetail>, data, context, counterpartyId)
  const mappedNodeData = getData(node as TypedRowNode<NegotiationPackageDetail>, node.data, context, counterpartyId)

  const mappedNode = {
    ...params.node,
    data: mappedNodeData
  }

  return {
    ...params,
    node: mappedNode,
    data: mappedData,
    context: { ...params.context, originalParams: params }
  }
}

export const clickEventPivotedRowCellDataMapper = (params: CellClickedEvent<NegotiationPackageDetail>) => {
  const {
    colDef: { cellRendererParams }
  } = params
  return !isNil(cellRendererParams?.valueMapper) ? cellRendererParams.valueMapper(params) : params
}

export const hasNegotiations = (data: NegotiationOrAuditDetail | undefined): data is NegotiationPackageDetail => {
  if (!isObject(data) || !('negotiations' in data)) {
    return false
  }
  return isObject(data[NEGOTIATIONS_PATH])
}

export const isAONSummary = (data: NegotiationOrAuditDetail) => data.negotiation.negotiationType === NegotiationType.CONTINGENT_SUMMARY

export const isAONOrSmartGroupSummary = (data: NegotiationOrAuditDetail) =>
  isAONSummary(data) || data.negotiation.negotiationType === NegotiationType.SMART_GROUPING_SUMMARY
